import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Check,
  CreditCard,
  Loader2,
  Calendar,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '@/config';
const API = `${BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, orgRes] = await Promise.all([
        axios.get(`${API}/admin/subscription-tiers/public`),
        api.get('/organisations/my'),
      ]);
      setTiers(tiersRes.data);
      setOrgInfo(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId, cycle) => {
    setSubscribing(`${tierId}-${cycle}`);
    try {
      const res = await api.post('/organisations/subscription', {
        tier_id: tierId,
        billing_cycle: cycle,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success('Subscription updated!');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['org_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  const hasActiveSub = orgInfo?.subscription_tier_id && orgInfo?.subscription_end_date;

  return (
    <DashboardLayout requiredRoles={['org_admin']}>
      <div className="space-y-6" data-testid="subscription-page">
        <div>
          <h1 className="text-2xl font-bold text-[#2a3e5d]">Subscription Plans</h1>
          <p className="text-sm text-[#1e293b]/60 mt-1">
            Choose a plan that fits your organisation's needs
          </p>
        </div>

        {/* Current Subscription Info */}
        {hasActiveSub && (
          <Card className="border-[#20acf6]/30 bg-[#f8fafc]/30" data-testid="current-subscription">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#20acf6] rounded-xl flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e293b]">Current Plan</h3>
                  <p className="text-sm text-[#1e293b]/60">
                    {orgInfo.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} billing
                    {orgInfo.subscription_end_date && (
                      <> &middot; Renews {new Date(orgInfo.subscription_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                    )}
                  </p>
                </div>
              </div>
              <Badge className="bg-[#f8fafc] text-[#2a3e5d]">Active</Badge>
            </CardContent>
          </Card>
        )}

        {/* Subscription Tiers */}
        <div className="grid md:grid-cols-2 gap-6">
          {tiers.map((tier) => (
            <Card key={tier.id} className="border-[#f8fafc] hover:shadow-md transition-shadow" data-testid={`tier-${tier.id}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#f8fafc] rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-[#02d1d4]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-[#1e293b]">{tier.name}</CardTitle>
                    <CardDescription>Up to {tier.user_limit} team members</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Monthly Option */}
                <div className="p-4 border border-[#f8fafc] rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-[#1e293b]">£{tier.price_monthly.toFixed(0)}</span>
                        <span className="text-sm text-[#1e293b]/50">/month</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3 text-[#1e293b]/40" />
                        <span className="text-xs text-[#1e293b]/40">Billed monthly, cancel anytime</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleSubscribe(tier.id, 'monthly')}
                    disabled={subscribing === `${tier.id}-monthly`}
                    className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                    data-testid={`subscribe-monthly-${tier.id}`}
                  >
                    {subscribing === `${tier.id}-monthly` ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Choose Monthly
                  </Button>
                </div>

                {/* Annual Option */}
                <div className="p-4 border-2 border-[#02d1d4] rounded-xl relative">
                  <Badge className="absolute -top-3 left-4 bg-[#02d1d4] text-white">Best Value</Badge>
                  <div className="flex items-center justify-between mb-3 mt-1">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-[#1e293b]">£{tier.price_yearly.toFixed(0)}</span>
                        <span className="text-sm text-[#1e293b]/50">/year</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="h-3 w-3 text-[#02d1d4]" />
                        <span className="text-xs text-[#02d1d4]">
                          Save £{((tier.price_monthly * 12) - tier.price_yearly).toFixed(0)} per year
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleSubscribe(tier.id, 'yearly')}
                    disabled={subscribing === `${tier.id}-yearly`}
                    className="w-full bg-[#02d1d4] hover:bg-[#00acd0] text-[#1e293b]"
                    data-testid={`subscribe-yearly-${tier.id}`}
                  >
                    {subscribing === `${tier.id}-yearly` ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Choose Annual
                  </Button>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {[
                    `Up to ${tier.user_limit} team members`,
                    'Unlimited course access',
                    'Team progress reporting',
                    'Certificate management',
                    'Email support',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#1e293b]/70">
                      <Check className="h-4 w-4 text-[#2a3e5d] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {tiers.length === 0 && (
          <Card className="border-[#f8fafc]">
            <CardContent className="p-12 text-center">
              <CreditCard className="h-12 w-12 text-[#02d1d4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1e293b] mb-2">No plans available</h3>
              <p className="text-sm text-[#1e293b]/60">Please contact support for subscription information.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;
